/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { overlapMethodsDetails } from 'src/clu/appShell/cluEntityDetailsPage/components/cluEntityCombinationOptionsDetails';
import { OverlapMethodCard } from 'src/clu/appShell/cluEntityDetailsPage/components/OverlapMethodCard';
import { cluTeachingBubbleMessages } from 'src/clu/cluTeachingBubbleMessages';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { EntityCompositionSetting } from 'src/core/types/EntitySwagger';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';

export type CombinationOptionDetails = { id: string; text: string; description: JSX.Element; key: EntityCompositionSetting };
export type CluEntityCombinationOptionCardsProps = {
    isSaveOperationRunning: boolean;
    entityCombinationOption: EntityCompositionSetting;
    onOptionClick: (option: EntityCompositionSetting) => void;
    onLearnMethodMoreClick: (methodKey: EntityCompositionSetting) => void;
};

/**
 * Component displaying and managing an entity's overlapping method'.
 */
export const CluEntityCombinationOptionCards = observer((props: CluEntityCombinationOptionCardsProps) => {
    const { entityCombinationOption, isSaveOperationRunning, onOptionClick, onLearnMethodMoreClick } = props;
    const { uiStore } = useGlobalState();
    const { localize } = useLocalization();

    const defaultCardIdRef = React.useRef<string>(getId());

    React.useEffect(() => () => dequeueTeachingBubble('CluLongestOverlapCard'), []);

    React.useEffect(() => {
        if (!uiStore.teachingBubbles.CluLongestOverlapCard) {
            createTeachingBubble(
                'CluLongestOverlapCard',
                localize(cluTeachingBubbleMessages.longestOverlapCardBody),
                localize(cluTeachingBubbleMessages.longestOverlapCardTitle),
                `#${defaultCardIdRef.current}`,
                true,
                true
            );
        }
    }, [Object.values(uiStore.teachingBubbles)]);

    return (
        <Stack horizontal verticalAlign="end" wrap tokens={{ childrenGap: 20 }}>
            {overlapMethodsDetails({ defaultCardIdRef: defaultCardIdRef.current })
                .filter(
                    method =>
                        entityCombinationOption === method.methodKey ||
                        LuisConstants.Clu.HiddenEntityOptions.every(methodKey => methodKey !== method.methodKey)
                )
                .map(methodProps => (
                    <OverlapMethodCard
                        {...methodProps}
                        onChangeMethodClick={onOptionClick}
                        currentMethod={entityCombinationOption}
                        description={methodProps.descriptionElement}
                        isSaveOperationRunning={isSaveOperationRunning}
                        onLearnMoreClick={() => onLearnMethodMoreClick(methodProps.methodKey)}
                    />
                ))}
        </Stack>
    );
});
